import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useDeleteLocation, useLocationsList } from '../api';
import type { LocationRow } from '../types';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS, BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';

export function LocationListPage() {
  const navigate = useNavigate();
  const canView = usePermission('Manage Locations.Location List', BASE_PERMISSION.VIEW);
  const canEdit = usePermission('Manage Locations.Location List', BASE_PERMISSION.EDIT);
  const canDelete = usePermission('Manage Locations.Location List', BASE_PERMISSION.DELETE);
  const canBulkAdd = usePermission('Manage Locations.Bulk Location Add', BASE_PERMISSION.ADD);
  const canBulkDelete = usePermission('Manage Locations.Bulk Location Delete', BASE_PERMISSION.DELETE);
  const query = useLocationsList();
  const rows = useModuleFilter(query.data ?? [], MODULE_LISTING_KEYS.MANAGE_LOCATIONS, (l) => l.locationId);

  const [pendingDelete, setPendingDelete] = useState<LocationRow | null>(null);
  const deleteMutation = useDeleteLocation();

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.locationId, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.locationName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the location'),
    });
  };

  const columns = useMemo<ColumnDef<LocationRow>[]>(
    () => [
      {
        accessorKey: 'locationId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.locationId}
          </span>
        ),
      },
      {
        accessorKey: 'locationName',
        header: 'Location name',
        cell: ({ row }) => (
          <Link
            to={`/update-location/${row.original.locationId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.locationName}
          </Link>
        ),
      },
      {
        accessorKey: 'reqDate',
        header: 'Created',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.reqDate),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) =>
          !canEdit && !canDelete ? null : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                  <Settings2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{row.original.locationName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {canEdit && (
                  <DropdownMenuItem
                    onClick={() => navigate(`/update-location/${row.original.locationId}`)}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem
                    onClick={() => setPendingDelete(row.original)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ),
      },
    ],
    [navigate, canEdit, canDelete],
  );

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Locations"
        description="Geographic / logical locations users and dialers can be scoped to."
        actions={
          <div className="flex items-center gap-2">
            {canBulkAdd && (
              <Button asChild variant="outline">
                <Link to="/bulk-addition-location">
                  <UserPlus className="h-4 w-4" />
                  Bulk add
                </Link>
              </Button>
            )}
            {canBulkDelete && (
              <Button asChild variant="outline">
                <Link to="/bulk-location-delete">
                  <UserMinus className="h-4 w-4" />
                  Bulk delete
                </Link>
              </Button>
            )}
            <Button asChild>
              <Link to="/create-location">
                <Plus className="h-4 w-4" />
                Add location
              </Link>
            </Button>
          </div>
        }
      />
      <DataTable<LocationRow, unknown>
        tableId="locations:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search locations…"
        emptyTitle="No locations yet"
        emptyDescription="Add a location to start scoping campaign delivery."
        getRowId={(row) => String(row.locationId)}
        exportFilename="locations.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">{pendingDelete?.locationName}</span> will
            be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </FillPage>
  );
}
